<template>
  <div class="airalo-pages">
    <PageTitle
      :title="$t('components.main.navigation.airalo.careers.job-vacancies')"
      :bordered="true"
      :back-button="{
        url: 'airalo-careers-working-at-airalo',
        hiddenAfterSizes: 'sm',
      }"
    />

    <div class="px-20 px-md-30 py-30 pt-md-0 pb-md-30 pb-lg-40">
      <breadcrumb :breadcrumbs="breadcrumbs" />

      <div class="new-container mx-auto">
        <div class="display-grid grid-cols-1 grid-cols-md-10 grid-cols-lg-12 gap-10 w-100">
          <div class="col-span-md-10 col-start-lg-2">
            <Card
              :title="heroContent.title"
              :description-element="heroContent.description"
              :image-url="`/assets/images/airalo-careers/join-the-airalo-team-v2.png`"
              :is-reverse="true"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="px-20 px-md-30 pt-30 pt-lg-40 bg-white">
      <div class="new-container mx-auto">
        <div class="display-flex flex-column justify-content-start align-items-start">
          <h2
            class="typo-h3 typo-md-h1 mb-0 w-100"
            :class="{
              'text-left': $dir() === 'ltr',
              'text-right': $dir() === 'rtl',
            }"
          >
            {{ $t('scenes.job.vacancies.current-opportunities-title') }}
          </h2>

          <h3
            class="display-none display-md-block typo-h3 typo-md-h4-right mb-0 w-100 mt-40"
            :class="{
              'text-left': $dir() === 'ltr',
              'text-right': $dir() === 'rtl',
            }"
          >
            {{ $t('scenes.job.vacancies.choose-a-department-title') }}
          </h3>
        </div>
        <div id="department-list">
          <div
            class="display-none display-md-flex flex-wrap align-items-center justify-content-start gap-20 gap-md-30 w-100 mt-30 pb-50"
          >
            <div
              :class="[
                'shadowed box-rounded-corner px-24 pt-17 pb-16 display-flex align-items-center gap-10 text-decoration-none tw-cursor-pointer',
                {
                  'color-text-white color-bg-primary': selectedDepartment === 'all',
                  'color-text-primary color-bg-white': selectedDepartment !== 'all',
                },
              ]"
              @click="onChangeFilter('all', 'department')"
            >
              <p class="typo-h6 mb-0">{{ $t('global.all') }}</p>
            </div>
            <div
              v-for="(item, index) in departments"
              :key="`departments-item-${index}`"
              :class="[
                'shadowed box-rounded-corner px-24 pt-17 pb-16 display-flex align-items-center gap-10 text-decoration-none tw-cursor-pointer',
                {
                  'color-text-white color-bg-primary': selectedDepartment === item.slug,
                  'color-text-primary color-bg-white': selectedDepartment !== item.slug,
                },
              ]"
              @click="onChangeFilter(item.slug, 'department')"
            >
              <p class="typo-h6 mb-0">{{ item.title }}</p>
              <p class="typo-p-small color-text-secondary mb-0">
                [<span
                  :class="{
                    'color-text-white': selectedDepartment === item.slug,
                    'color-text-primary': selectedDepartment !== item.slug,
                  }"
                  >{{ item.jobs }}</span
                >]
              </p>
            </div>
          </div>
          <div
            :class="[
              'w-100 display-md-none mt-20',
              {
                'pb-30': !isShowEmptyMessage && opportunities.paginatedList.length === 0,
              },
            ]"
            v-if="departmentsMobile && departmentsMobile.options"
          >
            <DropDown
              id="departments"
              :label="getDepartmentsMobileLabel('components.filter.dropdown.label.depatment-title')"
              :options="departmentsMobile.options"
              :selected="departmentsMobile.selected"
              @onChange="onChangeFilter($event, 'department')"
            />
          </div>
        </div>

        <template v-if="!isShowEmptyMessage && opportunities.paginatedList.length > 0">
          <div
            :class="[
              'display-none display-md-flex flex-wrap align-items-center justify-content-start gap-20 w-100 pt-20 border-t border-t-solid color-border-top-lighter',
            ]"
            dir="auto"
          >
            <h4 class="typo-h6 mb-0">
              {{ $t('scenes.job.vacancies.filter-title') }}
            </h4>
          </div>
          <div
            class="display-grid grid-cols-1 grid-cols-lg-3 gap-20 mt-20 mt-md-25 border-b border-b-solid color-border-bottom-lighter pb-25"
          >
            <template v-if="jobTitle.options.length > 0">
              <DropDown
                id="jobTitle"
                :label="jobTitle.label"
                :options="jobTitle.options"
                :selected="jobTitle.selected"
                direction="auto"
                @onChange="onChangeFilter($event, 'title')"
              />
            </template>

            <template v-if="countries.options.length > 0">
              <DropDown
                id="countries"
                :label="countries.label"
                :options="countries.options"
                :selected="countries.selected"
                direction="auto"
                @onChange="onChangeFilter($event, 'location')"
              />
            </template>

            <template v-if="roleType.options.length > 0">
              <DropDown
                id="roleType"
                :label="roleType.label"
                :options="roleType.options"
                :selected="roleType.selected"
                direction="auto"
                @onChange="onChangeFilter($event, 'commitment')"
              />
            </template>
          </div>
          <div
            class="display-grid grid-cols-1 grid-cols-sm-2 grid-cols-md-3 grid-cols-lg-4 gap-10 gap-md-22 mt-25 pb-30 pb-lg-40"
            dir="auto"
          >
            <div
              v-for="(item, index) in opportunities.paginatedList"
              :key="`opportunities-item-${index}`"
              class="box-rounded shadowed p-25 p-md-24 display-flex flex-column justify-content-start align-items-start minh-260 z-index-5 position-relative"
            >
              <div class="border-b border-b-solid color-border-bottom-very-lighter pb-10 w-100">
                <p class="typo-h6 color-text-secondary mb-0">
                  {{ getLocationText(item.location) }}
                </p>
              </div>

              <div class="mt-20 mb-10 w-100">
                <a class="typo-h4-right color-text-primary mb-0" :href="item.urls.show" target="_blank">
                  {{ item.title }}
                </a>
              </div>

              <div class="mt-auto w-100">
                <p class="typo-p-small color-text-secondary mb-0">- {{ item.commitment }}</p>
                <p class="typo-p-small color-text-secondary mb-0 mt-10">- {{ item.department }}</p>
              </div>
              <template v-if="item.urls && item.urls.apply && item.urls.show">
                <div class="mt-20 w-100">
                  <a :href="item.urls.show" target="_blank" class="button button-secondary-outline small-md w-100">
                    {{ $t('global.view-details', 'en') }}
                  </a>
                  <a
                    :href="item.urls.apply"
                    target="_blank"
                    class="button button-secondary small-md tw-bordertw-border tw-border-solid tw-border-primary w-100 tw-mt-2.5 md:tw-mt-4"
                  >
                    {{ $t('global.apply-now', 'en') }}
                  </a>
                </div>
              </template>
            </div>
            <client-only>
              <infinite-loading
                :identifier="infiniteId"
                @infinite="infiniteScroll"
                v-if="opportunities.filtered && opportunities.filtered.length > 0"
              >
                <div slot="spinner">
                  <div class="load-more-preloader mt-10 relative">
                    <client-only>
                      <v-lottie position="fixed" />
                    </client-only>
                  </div>
                </div>
                <div slot="no-more"></div>
                <div slot="no-results"></div>
              </infinite-loading>
            </client-only>
          </div>
        </template>

        <template v-else-if="isShowEmptyMessage">
          <div class="no-jobs py-40 py-lg-0 display-flex justify-content-center align-items-end">
            <div
              class="display-flex justify-content-center justify-content-lg-between align-items-end w-100"
              style="min-height: 135px"
            >
              <div class="no-jobs-image display-none display-lg-flex justify-content-start align-items-end">
                <nuxt-img
                  src="/assets/images/airalo-careers/no-jobs-1.svg"
                  alt=""
                  height="465"
                  :class="{
                    'w-100 h-auto': !deviceType.isMobile,
                    'w-auto h-100': deviceType.isMobile,
                  }"
                  loading="lazy"
                />
              </div>
              <p class="typo-h5-regular color-text-secondary text-center w-100 mb-auto mt-auto">
                {{ $t('scenes.job.vacancies.empty-state') }}
              </p>
              <div class="no-jobs-image display-none display-lg-flex">
                <nuxt-img
                  src="/assets/images/airalo-careers/no-jobs-2.svg"
                  alt=""
                  height="465"
                  :class="{
                    'w-100 h-auto': !deviceType.isMobile,
                    'w-auto h-100': deviceType.isMobile,
                  }"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div class="px-20 px-md-30 py-30 py-lg-40">
      <div class="new-container mx-auto">
        <div>
          <BottomBanner image-url="/assets/images/airalo-careers/job-vacancies-banner.jpg">
            <div class="display-flex flex-column justify-content-center align-items-center w-100">
              <h2 class="typo-h1 mb-0">
                {{ $t('components.banner.job.vacancies.title') }}
              </h2>
              <p class="typo-p mb-0 mt-10 text-center">
                {{ $t('components.banner.job.vacancies.subtitle') }}
              </p>
              <nuxt-link
                @click.native="
                  sendEventHandler({
                    text: $t('components.banner.job.vacancies.button-title'),
                    url: localePath({ name: 'airalo-careers-working-at-airalo' }),
                  })
                "
                class="button button-secondary btn-primary-hv shadowed minw-224 mt-20 text-center"
                :to="
                  localePath({
                    name: 'airalo-careers-working-at-airalo',
                  })
                "
              >
                {{ $t('components.banner.job.vacancies.button-title') }}
              </nuxt-link>
            </div>
          </BottomBanner>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import InfiniteLoading from 'vue-infinite-loading'

  import PageTitle from '~/components/shared/page-title.vue'
  import Card from '~/components/card-boxes/side-with-image'
  import BottomBanner from '~/components/bottom/banner'
  import Breadcrumb from '~/components/partner-with-us/breadcrumb'
  import DropDown from '~/components/input/dropdown'

  export default {
    components: {
      PageTitle,
      Card,
      Breadcrumb,
      BottomBanner,
      DropDown,
      InfiniteLoading,
    },

    data() {
      return {
        preloader: true,
        isShowEmptyMessage: false,

        breadcrumbs: [
          {
            title: this.$t('components.main.navigation.airalo.careers.working-at-airalo'),
            link: '/airalo-careers/working-at-airalo',
          },
          {
            title: this.$t('components.main.navigation.airalo.careers.job-vacancies'),
          },
        ],

        heroContent: {
          title: '',
          description: '',
        },

        departments: [],
        departmentsMobile: {
          selected: { value: 'all', text: this.$t('global.all') },
          options: [{ value: 'all', text: this.$t('global.all') }],
        },

        selectedDepartment: 'all',

        departmentsMobileDefault: {
          default: { value: 'all', text: this.$t('global.all') },
          selected: { value: 'all', text: this.$t('global.all') },
        },

        opportunities: {
          limit: 200,
          state: '',
          list: [],
          filtered: [],
          filters: [],
          paginatedList: [],
          pagination: {
            current: 0,
            size: 16,
          },
        },

        infiniteId: +new Date(),

        jobTitle: {
          label: this.$t('components.filter.dropdown.label.job-title'),
          default: { value: 'all', text: this.$t('global.all') },
          selected: { value: 'all', text: this.$t('global.all') },
          options: [{ value: 'all', text: this.$t('global.all') }],
        },

        countries: {
          label: this.$t('components.filter.dropdown.label.country'),
          default: { value: 'all', text: this.$t('global.all') },
          selected: { value: 'all', text: this.$t('global.all') },
          options: [{ value: 'all', text: this.$t('global.all') }],
        },

        roleType: {
          label: this.$t('components.filter.dropdown.label.role-type'),
          default: { value: 'all', text: this.$t('global.all') },
          selected: { value: 'all', text: this.$t('global.all') },
          options: [{ value: 'all', text: this.$t('global.all') }],
        },
      }
    },

    computed: {
      deviceType() {
        return this.$store.getters.get_device_types
      },
    },

    async fetch() {
      try {
        const responsesData = await Promise.all([
          this.$store.dispatch('fetch_contents_page', 'job-vacancies-hero'),
          this.$store.dispatch('fetch_teams'),
          this.$store.dispatch('fetch_jobs'),
        ])

        //  Job Vacancies Hero
        const heroContent = responsesData[0]

        this.heroContent.title = heroContent.title
        this.heroContent.description = this.$utils.sanitizeCoontents('p', heroContent.content)

        if (this.heroContent.description.length > 0) {
          this.heroContent.description = this.heroContent.description.join('\n')
        }

        //  Teams
        const departments = responsesData[1]

        this.departments = departments

        for (const element of departments) {
          this.departmentsMobile.options.push({
            value: element.slug,
            text: element.title,
          })
        }

        //  Jobs
        this.opportunities.list = [...responsesData[2]]

        if (this.$route.query.department) {
          this.selectedDepartment = this.$route.query.department
          this.departmentsMobile.selected = {
            value: this.$route.query.department,
            text: this.$route.query.department,
          }

          if (this.$route.query.department !== 'all') {
            this.opportunities.filters = [
              {
                key: 'department',
                value: this.$route.query.department,
              },
            ]
          }

          this.opportunities.filtered = this.opportunities.list

          this.setFilterToList(true)
        } else {
          this.opportunities.filtered = this.opportunities.list
          this.setFilterToList(false)
        }
      } catch (err) {
        throw new Error(err)
      } finally {
        this.preloader = false
      }
    },

    scrollToTop: true,

    methods: {
      setFilterToList(isDep) {
        this.resetPagination()

        this.opportunities.filtered = this.opportunities.list

        if (this.opportunities.filters.length > 0) {
          for (const filter of this.opportunities.filters) {
            this.opportunities.filtered = this.opportunities.filtered.filter((v) => v[filter.key] === filter.value)
          }
        } else {
          this.opportunities.filtered = [...this.opportunities.list]
        }

        if (isDep) {
          this.setFiltersOptions()
        }

        this.paginateList()
      },

      resetPagination() {
        this.opportunities.pagination.current = 0
        this.opportunities.paginatedList = []
        this.infiniteId += 1
      },

      paginateList() {
        if (this.opportunities.filtered.length > this.opportunities.pagination.size) {
          this.opportunities.paginatedList = [
            ...this.opportunities.paginatedList,
            ...this.opportunities.filtered.slice(
              this.opportunities.pagination.current,
              this.opportunities.pagination.current + this.opportunities.pagination.size
            ),
          ]

          this.opportunities.pagination.current += this.opportunities.pagination.size
        } else {
          this.opportunities.paginatedList = [...this.opportunities.filtered]
        }

        this.isShowEmptyMessage = this.selectedDepartment && this.opportunities.paginatedList.length === 0
      },

      setFiltersOptions() {
        let allJobTitle = []
        let allCountries = []
        let allRoleType = []

        for (const element of this.opportunities.filtered) {
          allJobTitle.push(element.title)
          allCountries.push(element.location)
          allRoleType.push(element.commitment)
        }

        allJobTitle = [...new Set(allJobTitle)]
        allCountries = [...new Set(allCountries)]
        allRoleType = [...new Set(allRoleType)]

        this.jobTitle.options = [this.jobTitle.default, ...allJobTitle.map((v) => ({ value: v, text: v }))]

        this.countries.options = [this.countries.default, ...allCountries.map((v) => ({ value: v, text: v }))]

        this.roleType.options = [this.roleType.default, ...allRoleType.map((v) => ({ value: v, text: v }))]
      },

      infiniteScroll($state) {
        setTimeout(() => {
          if (this.opportunities.paginatedList.length < this.opportunities.filtered.length) {
            this.paginateList()
            $state.loaded()
          } else {
            $state.complete()
          }
        }, 500)
      },

      onChangeFilter(payload, filterName) {
        if (filterName === 'department') {
          this.selectedDepartment = payload
          this.departmentsMobile.selected = {
            value: payload,
            text: payload,
          }
          history.pushState({}, null, `${this.$route.path}?department=${payload}`)
        }
        if (payload === 'all') {
          this.opportunities.filters = this.opportunities.filters.filter((v) => v.key !== filterName)
        } else {
          if (!this.opportunities.filters.find((v) => v.key === filterName)) {
            this.opportunities.filters.push({
              key: filterName,
              value: payload,
            })
          } else {
            this.opportunities.filters.map((v) => {
              if (v.key === filterName) {
                v.value = payload
              }
              return v
            })
          }
        }

        this.setFilterToList(false)
      },

      getLocationText(text) {
        return text.toUpperCase()
      },

      onChangeMobileDepartment(payload, filterName) {
        if (payload && payload.length > 0 && payload !== 'all') {
          this.$router.push(
            this.localePath({
              name: 'airalo-careers-job-vacancies',
              query: {
                department: payload,
              },
            })
          )
        } else {
          this.$router.push(
            this.localePath({
              name: 'airalo-careers-job-vacancies',
            })
          )
        }
      },

      getDepartmentsMobileLabel(key) {
        return this.$t(key)
      },

      sendEventHandler(payload) {
        this.$utils.sendEvent('ctaClick', payload)
      },
    },
    head() {
      return {
        title: this.$te('seo.airalo.careers.job-vacancies-title')
          ? this.$t('seo.airalo.careers.job-vacancies-title')
          : this.$t('seo.homepage.title'),
        meta: [
          {
            hid: 'description',
            name: 'description',
            content: this.$t('seo.airalo.careers.job-vacancies-description'),
          },
          {
            hid: 'keywords',
            name: 'keywords',
            content: this.$t('seo.airalo.careers.job-vacancies-keywords'),
          },
          {
            property: 'og:title',
            content: this.$t('seo.airalo.careers.job-vacancies-title'),
          },
          {
            property: 'og:description',
            content: this.$t('seo.airalo.careers.job-vacancies-description'),
          },
        ],
      }
    },
  }
</script>

<style lang="scss" scoped>
  .middle-banner {
    @media only screen and (max-width: 767px) {
      height: 360px;
      overflow: hidden;
      position: relative;

      img {
        position: absolute;
        left: 50%;
        top: 0;
        transform: translateX(-50%);
      }
    }
  }

  .no-jobs {
    @media only screen and (min-width: 992px) {
      min-height: 255px;
    }

    p {
      max-width: 410px;
    }
  }
</style>
